// import { useState } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
// import { Input } from '@/components/ui/input'
// import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
// import { useToast } from '@/components/ui/use-toast'
import { Trash2, Loader2 } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { School } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useEffect } from 'react'

interface Class {
  id: string
  name: string
}

interface ClassesConfigurationProps {
  classes: Class[]
  isLoading?: boolean
  onAddClass: (classItem: Class) => void
  onDeleteClass: (classId: string) => void
  refetchClasses?: () => Promise<any>
}

export function ClassesConfiguration({
  classes,
  isLoading = false,
  // onAddClass,
  onDeleteClass,
  refetchClasses,
}: ClassesConfigurationProps) {
  const { t } = useTranslation('timetable', { keyPrefix: 'timetable-folder.classesConfiguration' })
  // const { toast } = useToast()
  // const [newClassName, setNewClassName] = useState('')

  // const handleAddClass = () => {
  //   if (!newClassName.trim()) {
  //     toast({
  //       title: 'Error',
  //       description: 'Class name cannot be empty',
  //       variant: 'destructive',
  //     })
  //     return
  //   }

  //   // Check if class already exists
  //   if (
  //     classes.some(
  //       (classItem) =>
  //         classItem.name.toLowerCase() === newClassName.trim().toLowerCase()
  //     )
  //   ) {
  //     toast({
  //       title: 'Error',
  //       description: 'This class already exists',
  //       variant: 'destructive',
  //     })
  //     return
  //   }

  //   // Generate a unique ID using timestamp
  //   const newClass: Class = {
  //     id: `class_${Date.now()}`,
  //     name: newClassName.trim(),
  //   }

  //   onAddClass(newClass)
  //   setNewClassName('')

  //   toast({
  //     title: 'Class Added',
  //     description: `${newClassName} has been added to the classes list`,
  //   })
  // }

  // const handleKeyPress = (e: React.KeyboardEvent) => {
  //   if (e.key === 'Enter') {
  //     handleAddClass()
  //   }
  // }

  // Check for invalid data and trigger refetch if needed
  useEffect(() => {
    // Only check after loading is complete
    if (!isLoading && refetchClasses) {
      // Check if we have classes but they're invalid
      const hasClasses = classes && classes.length > 0
      const invalidClasses = hasClasses 
        ? classes.filter(c => !c || !c.id || !c.name || c.name.trim() === '')
        : []
      
      // If we have invalid classes (all classes are invalid or some are invalid)
      if (invalidClasses.length > 0) {
        console.warn('⚠️ ClassesConfiguration: Invalid data detected, refetching...', {
          invalidCount: invalidClasses.length,
          totalCount: classes?.length || 0
        })
        
        // Use refetch function with a small delay to avoid race conditions
        const timeoutId = setTimeout(() => {
          refetchClasses().catch((error) => {
            console.error('Failed to refetch classes:', error)
            // Fallback to reload if refetch fails after retry
            setTimeout(() => {
              if (refetchClasses) {
                refetchClasses().catch(() => {
                  window.location.reload()
                })
              } else {
                window.location.reload()
              }
            }, 2000)
          })
        }, 500)
        
        return () => clearTimeout(timeoutId)
      }
    }
  }, [classes, isLoading, refetchClasses])

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <School className="h-4 w-4" />
          <AlertTitle>{t('infoTitle')}</AlertTitle>
          <AlertDescription>
            {t('infoDescription')}
          </AlertDescription>
        </Alert>
        {/*  the add function need logic */}
        {/* <div className="flex items-end gap-4">
          <div className="flex-1">
            <Label htmlFor="class-name">{t('table.className')}</Label>
            <Input
              id="class-name"
              placeholder="e.g., Class 10A or Grade 5"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={isLoading}
            />
          </div>
          <Button
            onClick={handleAddClass}
            disabled={isLoading || !newClassName.trim()}
          >
            <Plus className="h-4 w-4 mr-1" />
            {t('addClass')}
          </Button>
        </div> */}

        <Separator />

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin mr-2" />
            <p>{t('loading')}</p>
          </div>
        ) : classes.length === 0 ? (
          <div className="text-center p-4 border rounded-md bg-muted/20">
            {t('empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('table.className')}</TableHead>
                  <TableHead className="w-[100px]">{t('table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {classes
                  .filter((classItem) => classItem && classItem.id && classItem.name && classItem.name.trim() !== '')
                  .map((classItem) => (
                    <TableRow key={classItem.id}>
                      <TableCell>{classItem.name}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDeleteClass(classItem.id)}
                          title={t('deleteTitle')}
                          className="min-h-[44px] min-w-[44px]"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>

            <div className="mt-4 text-sm text-muted-foreground">
              {t('total', { count: classes.length })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
